import { useState, useEffect } from "react";
import {
  X,
  Save,
  AlertCircle,
  Check,
  Package,
  Eye,
  EyeOff,
} from "lucide-react";
import type { CatalogSection } from "../../api";
import { VariantsList, type SectionVariant } from "./VariantsList";
import { SectionPreview } from "./SectionPreview";

interface SectionFormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  section?: { id: string; data: CatalogSection };
  existingSectionIds: string[];
  availableSlots: string[];
  onSave: (id: string, section: CatalogSection) => Promise<void>;
}

export function SectionFormDialog({
  isOpen,
  onClose,
  section,
  existingSectionIds,
  availableSlots,
  onSave,
}: SectionFormDialogProps) {
  const isEditMode = !!section;

  // Form state
  const [sectionId, setSectionId] = useState("");
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [selectedSlots, setSelectedSlots] = useState<string[]>([]);
  const [variants, setVariants] = useState<SectionVariant[]>([]);

  // UI state
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Initialize form with section data (edit mode)
  useEffect(() => {
    if (isEditMode && section) {
      setSectionId(section.id);
      setLabel(section.data.label || "");
      setDescription(section.data.description || "");
      setSelectedSlots(section.data.allowedSlots || []);

      // Normalize variants to object format
      const sectionVariants = section.data.variants || [];
      if (
        sectionVariants.length > 0 &&
        typeof sectionVariants[0] === "string"
      ) {
        setVariants(
          (sectionVariants as string[]).map((id) => ({ id, label: id })),
        );
      } else {
        setVariants(sectionVariants as SectionVariant[]);
      }
    } else {
      // Reset form (create mode)
      setSectionId("");
      setLabel("");
      setDescription("");
      setSelectedSlots([]);
      setVariants([]);
    }
    setError(null);
    setSaveSuccess(false);
  }, [isEditMode, section, isOpen]);

  // Keyboard shortcuts: Escape to close
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isSaving) {
        handleClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, isSaving]);

  // Validation
  const validateForm = (): string | null => {
    // ID validation
    if (!sectionId.trim()) {
      return "L'ID sezione è obbligatorio";
    }

    // ID format: lowercase, hyphens, no spaces
    if (!/^[a-z0-9-]+$/.test(sectionId)) {
      return "L'ID sezione deve contenere solo lettere minuscole, numeri e trattini";
    }

    // Check unique ID (only in create mode or if ID changed)
    if (!isEditMode || sectionId !== section?.id) {
      if (existingSectionIds.includes(sectionId)) {
        return `L'ID sezione "${sectionId}" esiste già`;
      }
    }

    // Label validation
    if (!label.trim()) {
      return "L'etichetta è obbligatoria";
    }

    // Allowed slots validation
    if (selectedSlots.length === 0) {
      return "Seleziona almeno uno slot consentito";
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    // Prepare section data
    const sectionData: CatalogSection = {
      allowedSlots: selectedSlots,
      variants: variants as SectionVariant[] | string[],
      label: label.trim(),
      description: description.trim() || undefined,
      props: section?.data.props, // Keep existing props
      variantProps: section?.data.variantProps,
    };

    // Save
    try {
      setIsSaving(true);
      await onSave(sectionId, sectionData);

      setSaveSuccess(true);

      // Close after short delay
      setTimeout(() => {
        handleClose();
      }, 1000);
    } catch (err: any) {
      setError(err.message || "Errore durante il salvataggio della sezione");
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (isSaving) return;
    setSectionId("");
    setLabel("");
    setDescription("");
    setSelectedSlots([]);
    setVariants([]);
    setError(null);
    setSaveSuccess(false);
    onClose();
  };

  const toggleSlot = (slot: string) => {
    setSelectedSlots((prev) =>
      prev.includes(slot) ? prev.filter((s) => s !== slot) : [...prev, slot],
    );
  };

  if (!isOpen) return null;

  // Build preview data from current form state
  const previewData = {
    id: sectionId || "section-id",
    label: label || "Nuova Sezione",
    description: description || undefined,
    allowedSlots: selectedSlots,
    variants: variants,
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        className={`bg-white rounded-lg shadow-xl w-full max-h-[90vh] overflow-hidden flex flex-col ${showPreview ? "max-w-6xl" : "max-w-2xl"}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Package className="w-5 h-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              {isEditMode
                ? `Modifica Sezione: ${section.id}`
                : "Crea Nuova Sezione"}
            </h2>
          </div>
          <div className="flex items-center space-x-2">
            {/* Preview Toggle */}
            <button
              type="button"
              onClick={() => setShowPreview(!showPreview)}
              className={`flex items-center space-x-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                showPreview
                  ? "bg-primary-100 text-primary-700 hover:bg-primary-200"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
              aria-label={
                showPreview ? "Nascondi anteprima" : "Mostra anteprima"
              }
            >
              {showPreview ? (
                <>
                  <EyeOff className="w-4 h-4" />
                  <span className="hidden sm:inline">Nascondi Anteprima</span>
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4" />
                  <span className="hidden sm:inline">Mostra Anteprima</span>
                </>
              )}
            </button>

            {/* Close Button */}
            <button
              onClick={handleClose}
              disabled={isSaving}
              className="p-1 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 disabled:opacity-50"
              aria-label="Chiudi finestra"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Split View: Form | Preview */}
        <div
          className={`flex-1 overflow-y-auto ${showPreview ? "grid grid-cols-2 divide-x divide-gray-200" : ""}`}
        >
          {/* Form Section */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
            <div className="px-6 py-4 space-y-4">
              {/* Error message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start space-x-2">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-red-800 font-medium">Errore</p>
                    <p className="text-sm text-red-700 mt-1">{error}</p>
                  </div>
                </div>
              )}

              {/* Success message */}
              {saveSuccess && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-start space-x-2">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-green-800 font-medium">
                      Sezione salvata con successo!
                    </p>
                  </div>
                </div>
              )}

              {/* Section ID */}
              <div>
                <label
                  htmlFor="sectionId"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  ID Sezione <span className="text-red-500">*</span>
                </label>
                <input
                  id="sectionId"
                  type="text"
                  value={sectionId}
                  onChange={(e) => {
                    setSectionId(
                      e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""),
                    );
                    setError(null);
                  }}
                  disabled={isEditMode} // Cannot change ID in edit mode
                  className="input w-full disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="es., hero-primary"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Solo lettere minuscole, numeri e trattini.{" "}
                  {isEditMode
                    ? "Non può essere modificato."
                    : "Deve essere univoco."}
                </p>
              </div>

              {/* Label */}
              <div>
                <label
                  htmlFor="label"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Etichetta <span className="text-red-500">*</span>
                </label>
                <input
                  id="label"
                  type="text"
                  value={label}
                  onChange={(e) => {
                    setLabel(e.target.value);
                    setError(null);
                  }}
                  className="input w-full"
                  placeholder="es., Hero Principale"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Nome leggibile visualizzato nell'interfaccia
                </p>
              </div>

              {/* Description */}
              <div>
                <label
                  htmlFor="description"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Descrizione
                </label>
                <textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  className="input w-full resize-none"
                  placeholder="Descrizione opzionale per questa sezione..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Opzionale. Aiuta a documentare lo scopo di questa sezione.
                </p>
              </div>

              {/* Allowed Slots */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Slot Consentiti <span className="text-red-500">*</span>
                </label>
                <div className="border border-gray-200 rounded-lg p-4 max-h-64 overflow-y-auto">
                  {availableSlots.length === 0 ? (
                    <p className="text-sm text-gray-500 italic">
                      Nessuno slot disponibile in questa versione del catalogo
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {availableSlots.map((slot) => (
                        <label
                          key={slot}
                          className="flex items-center space-x-3 p-2 rounded-md hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedSlots.includes(slot)}
                            onChange={() => toggleSlot(slot)}
                            className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                          />
                          <span className="text-sm text-gray-900 font-mono">
                            {slot}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Seleziona in quali slot questa sezione può essere posizionata.
                  Almeno uno richiesto.
                </p>
                {selectedSlots.length > 0 && (
                  <p className="text-xs text-green-600 mt-1">
                    {selectedSlots.length} slot selezionat
                    {selectedSlots.length > 1 ? "i" : "o"}
                  </p>
                )}
              </div>

              {/* Variants */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Varianti
                </label>
                <VariantsList variants={variants} onChange={setVariants} />
                <p className="text-xs text-gray-500 mt-1">
                  Definisci diverse variazioni di questa sezione (es., opzioni
                  di layout, stili).
                </p>
              </div>
            </div>
          </form>

          {/* Preview Section */}
          {showPreview && (
            <div className="overflow-y-auto">
              <div className="px-6 py-4">
                <SectionPreview
                  section={previewData}
                  availableSlots={availableSlots}
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSaving}
            className="btn-secondary"
          >
            Annulla
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={isSaving || saveSuccess}
            className="btn-primary"
          >
            {isSaving ? (
              <>
                <Save className="w-4 h-4 mr-2 animate-pulse" />
                Salvataggio...
              </>
            ) : saveSuccess ? (
              <>
                <Check className="w-4 h-4 mr-2" />
                Salvato!
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {isEditMode ? "Salva Modifiche" : "Crea Sezione"}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
